import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import { submitPartnerInquiry } from './index';
import Spinner from '../components/Spinner';

const PartnerWithUs = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    contactPerson: '',
    email: '',
    phone: '',
    businessType: 'Supermarket',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await submitPartnerInquiry(formData);
      setSuccess(true);
      setFormData({ businessName: '', contactPerson: '', email: '', phone: '', businessType: 'Supermarket', message: '' });
    } catch (err: any) {
      console.warn("Partner inquiry API failed, falling back to local storage.", err);

      // Fallback to save to localStorage for demo/offline purposes
      const newInquiry = {
        id: `partner-${Date.now()}`,
        ...formData,
        date: new Date().toISOString(),
        status: 'New'
      };

      const existingInquiries = JSON.parse(localStorage.getItem('kano_fresh_partner_inquiries') || '[]' );
      localStorage.setItem('kano_fresh_partner_inquiries', JSON.stringify([newInquiry, ...existingInquiries]));

      // Still show success to the user
      setSuccess(true);
      setFormData({ businessName: '', contactPerson: '', email: '', phone: '', businessType: 'Supermarket', message: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          
          {/* Info Section */}
          <div>
            <h1 className="text-4xl font-serif font-bold text-primary mb-6">Partner With Us</h1>
            <p className="text-lg text-gray-600 font-sans mb-6 leading-relaxed">
              Are you a supermarket, stall owner, restaurant, or hotel looking for reliable, high-quality fresh produce? 
              Mokagz Fresh Produce offers tailored B2B solutions to keep your shelves stocked and your customers happy.
            </p>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-6">
              <h3 className="text-xl font-bold text-primary mb-4">Why Partner With Us?</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Consistent supply of premium local & imported produce</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Competitive wholesale pricing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Priority delivery logistics</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Flexible payment terms for verified partners</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Form Section */}
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100">
            {success ? (
              <div className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Inquiry Received!</h2>
                <p className="text-gray-600">Thank you for reaching out. Our B2B team will review your details and contact you within 24 hours.</p>
                <button onClick={() => setSuccess(false)} className="mt-6 text-primary font-medium hover:underline">Send another inquiry</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Business Inquiry Form</h2>
                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded text-sm border border-red-100">
                    {error}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                  <input type="text" name="businessName" required value={formData.businessName} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                    <input type="text" name="contactPerson" required value={formData.contactPerson} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                    <select name="businessType" value={formData.businessType} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary bg-white">
                      <option value="Supermarket">Supermarket</option>
                      <option value="Stall">Market Stall</option>
                      <option value="Restaurant">Restaurant</option>
                      <option value="Hotel">Hotel</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">What are you looking for?</label>
                  <textarea name="message" rows={4} required value={formData.message} onChange={handleChange} placeholder="Tell us about the produce you need and estimated weekly quantities..." className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary"></textarea>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-primary text-white py-3 rounded-md font-bold hover:bg-green-800 transition disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <><Spinner /> Sending Inquiry...</> : 'Submit Inquiry'}
                </button>              
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerWithUs;